import { attr, css, FASTElement, html, observable, Updates } from '@microsoft/fast-element';
import { designTokens } from '../../design-tokens';
import { DesignToken, DesignTokenChangeRecord, DesignTokenSubscriber } from '../../foundation/design-token';
import { NatGenDesignSystem } from '../../natgen-design-system';
import { backgroundColorContext } from '../../context/background-context';

/**
 * @element natgen-icon
 */
export class NatGenIcon extends FASTElement implements DesignTokenSubscriber<DesignToken<any>> {
    protected currentIconName: string;

    @observable
    public iconContent: string;

    @observable
    public viewBox: string;

    @backgroundColorContext
    isDarkMode: boolean;

    @attr 
    alt: string;

    @attr({ mode: 'boolean', attribute: 'prefer-glyph' })
    preferGlyph: boolean = false;

    @attr
    name: string;
    nameChanged(previousValue: string, nextValue: string) {
        if(previousValue != nextValue) {
            if (previousValue && !previousValue.includes('/') && `icon-${previousValue}` in designTokens) {
                designTokens[`icon-${previousValue}`].token.unsubscribe(this);
            }

            Updates.enqueue(() => this.loadIcon());

            if(!nextValue.includes('/') && `icon-${nextValue}` in designTokens)
                designTokens[`icon-${nextValue}`].token.subscribe(this);
        }
    }

    getIconName(): string {
        if (!this.name.includes('/') && `icon-${this.name}` in designTokens) {
            // TODO: Glyph breakpoint token?
            if (this.preferGlyph || this.isDarkMode || this.getHeight() <= 28 && `icon-${this.name}-glyph` in designTokens)
                return designTokens[`icon-${this.name}-glyph`].getValueFor(this);
            else
                return designTokens[`icon-${this.name}`].getValueFor(this);
        }

        return this.name;
    }

    handleChange(token: DesignToken<any>, record: DesignTokenChangeRecord<DesignToken<any>>): void {
        if (record.token === designTokens[`icon-${this.name}`].token) {
            Updates.enqueue(() => this.loadIcon());
        }
    }

    loadIcon() {
        const iconName = this.getIconName();

        if(iconName != this.currentIconName)
        {
            this.currentIconName = iconName;

            var path = iconName.startsWith('//') ? `${iconName.substring(2)}.svg` : `${process.env.ASSET_PATH ? process.env.ASSET_PATH : ''}icons/${iconName}.svg`;

            // TODO: throw loaded event?
            fetch(path)
                .then(r => r.text())
                .then(text => {
                    const parser = new DOMParser();
                    const svgElement = parser.parseFromString(text, 'image/svg+xml').querySelector('svg');
                    this.viewBox = svgElement.getAttribute('viewBox');
                    this.iconContent = svgElement.innerHTML;
                })
                .catch(reason => console.warn(`Unknown icon ${this.name}`, reason));
        }
    }

    getHeight() {
        return this.size * designTokens['space-design-unit'].getValueFor(this);
    }

    getWidth() {
        const viewBoxSizes = this.viewBox?.split(' ').map(s => parseFloat(s));

        if(viewBoxSizes == null || viewBoxSizes.length != 4)
            return this.getHeight();

        return (this.getHeight() / viewBoxSizes[3]) * viewBoxSizes[2];
    }

    @attr
    size: number = 5;
}

export const natGenIconDefinition = NatGenIcon.compose({
    name: `${NatGenDesignSystem.prefix}-icon`,
    template: html<NatGenIcon>`
        <template style="height: calc(${x => x.getHeight()} * 1px); width: calc(${x => x.getWidth()} * 1px);">
            <svg 
                viewBox="${x => x.viewBox}" 
                :innerHTML="${x => x.iconContent}" 
                alt="${x => x.alt}" 
                aria-hidden="${x => x.alt ? 'false' : 'true'}"></svg>
        </template>
    `,
    styles: css`
        :host {
            display: inline-flex;
        }

        :host > svg {
            fill: currentColor;
            width: 100%;
            height: 100%;
        }
    `
});
